import React, { useMemo } from 'react';
import { Stack, Typography, Checkbox } from '@mui/material';
import { titleCase, formatKeyForDisplay } from '@/utils';
import { ConditionTab, PaintBlockCheckboxStyles, CheckboxLabel } from './style';
import { useGroupedDefects } from './hooks/useDefectsUtils';
import { PLATFORM_EXPORT_CORROSION_GROUPKEY } from '@/constants';

type Props = {
  defects: DefectLegend[] | undefined;
  selectedConditionType: string;
  setSelectedConditionType: React.Dispatch<React.SetStateAction<string>>;
  selectedCoatingCondition: string[];
  changeCoatingTab: (argument0: string) => void;
  allCoatingConditions: DefectLegend[] | undefined;
};

type DefectLegend = {
  id: number;
  name: string;
  groupKey: string;
  groupName: string;
  color?: string;
};

export const ConditionSelection = ({
  defects,
  selectedConditionType,
  setSelectedConditionType,
  selectedCoatingCondition,
  changeCoatingTab,
  allCoatingConditions,
}: Props) => {
  const groupedDefects = useGroupedDefects(defects);
  const CoatingCondition = formatKeyForDisplay(PLATFORM_EXPORT_CORROSION_GROUPKEY)
  const SortedDefectsGroupsKeys = useMemo(() => {
    const keys = groupedDefects ? Object.keys(groupedDefects) : [];
    const index = keys.indexOf(CoatingCondition);
    if (index !== -1) {
      keys.splice(index, 1);
      keys.unshift(CoatingCondition); // Move Coating Condition to the first index
    }
    return keys;
  }, [CoatingCondition, groupedDefects]);

  const CoatingConditionsSelections = useMemo(() => {
    return (
      <Stack alignItems="center" direction="row">
        {allCoatingConditions?.map((field) => (
          <Stack key={field.id} alignItems="center" direction="row" marginRight={1.5}>
            <Checkbox
              checked={selectedCoatingCondition.includes(field.name.toLowerCase())}
              data-testid="coating-checkbox"
              onClick={(event) => event.stopPropagation()}
              onChange={() => changeCoatingTab(field.name.toLowerCase())}
              sx={PaintBlockCheckboxStyles}
            />
            <CheckboxLabel>{titleCase(field.name)}</CheckboxLabel>
          </Stack>
        ))}
      </Stack>
    );
  }, [allCoatingConditions, changeCoatingTab, selectedCoatingCondition]);

  return (
    <Stack mb={2.5} direction="row" alignItems="center">
      <Stack direction="row" mr={2}>
        {groupedDefects &&
          SortedDefectsGroupsKeys.map((item) => (
            <ConditionTab
              selected={selectedConditionType === item}
              key={item}
              onClick={() => {
                setSelectedConditionType(item);
              }}
            >
              <Typography fontSize="1.2rem" fontWeight={600}>
                {item}
              </Typography>
            </ConditionTab>
          ))}
      </Stack>
      {CoatingConditionsSelections}
    </Stack>
  );
};
